import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { inject, injectable } from "inversify";
import { makeObservable } from "mobx";
import ApiService from "@/src/application/services/api-service";

type UnauthorizedHandler = () => void;

@injectable()
export default class ApiServiceImpl extends ApiService {
  private readonly baseUrl: string;
  private readonly axiosInstance: AxiosInstance;
  public readonly blankAxios: AxiosInstance;
  private onUnauthorized: UnauthorizedHandler | null = null;

  constructor(@inject("BASE_API") baseUrl: string) {
    super();
    this.baseUrl = baseUrl;
    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      withCredentials: true,
    });

    this.blankAxios = axios.create({
      baseURL: this.baseUrl,
      withCredentials: true,
    });

    makeObservable(this);
  }

  setUnauthorizedHandler(handler: UnauthorizedHandler) {
    this.onUnauthorized = handler;
    this.axiosInstance.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error.status === 401 && typeof window !== "undefined" && this.onUnauthorized) {
          this.onUnauthorized();
        }
        return Promise.reject(error);
      }
    );
  }

  async get<R, C = unknown>(endpoint: string, config?: AxiosRequestConfig<C>) {
    return this.axiosInstance.get<R>(endpoint, config);
  }

  async post<R, T = object>(endpoint: string, data: T): Promise<AxiosResponse<R, T>> {
    return await this.axiosInstance.post(endpoint, data);
  }

  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}
